import { useEffect, useState } from "react";

import { ensureCsrfToken } from "../../api";

import Swal from "sweetalert2";


const DEFAULT_OPEN_SECTIONS = {
  invoiceType: true,
  duplicate: false,
  companyPan: false,
  materialService: false,
};


function createDefaultConfig() {
  return {
    invoiceType: "Both",

    duplicate_check: {
      dup_check: "Y",
    },

    companyPan: "",

    material_service_codes: {
      material: [],
      service: [],
    },
  };
}


function mergeLoadedConfig(payload) {
  const next = createDefaultConfig();

  if (!payload) {
    return next;
  }

  if (payload.invoiceType) {
    next.invoiceType = payload.invoiceType;
  }

  if (payload.duplicate_check) {
    next.duplicate_check = {
      ...next.duplicate_check,
      ...payload.duplicate_check,
    };
  }

  if (typeof payload.pans === "string") {
    next.companyPan = payload.pans;
  }

  if (payload.material_service_codes) {
    next.material_service_codes = {
      material: Array.isArray(payload.material_service_codes.material)
        ? payload.material_service_codes.material
        : [],

      service: Array.isArray(payload.material_service_codes.service)
        ? payload.material_service_codes.service
        : [],
    };
  }

  return next;
}


async function parseJsonResponse(response) {
  const contentType = (
    response.headers.get("content-type") || ""
  ).toLowerCase();

  if (contentType.includes("application/json")) {
    return {
      isJson: true,
      payload: await response.json(),
      text: "",
    };
  }

  return {
    isJson: false,
    payload: null,
    text: await response.text(),
  };
}


function normalizeNonJsonError(response, text, fallback) {
  const body = String(text || "");
  const lower = body.toLowerCase();

  const looksLikeHtml =
    lower.includes("<html") ||
    lower.includes("<!doctype html");

  const looksLikeLogin =
    lower.includes("miro app login") ||
    lower.includes('name="password"');

  if (
    response.redirected ||
    response.url.includes("next=") ||
    looksLikeLogin
  ) {
    return "Session is not authenticated. Please login again.";
  }

  if (looksLikeHtml) {
    return fallback;
  }

  return body || fallback;
}


function Section({
  title,
  subtitle,
  open,
  onToggle,
  children,
}) {
  return (
    <article className="config-card">
      <button
        className="config-card__head"
        type="button"
        onClick={onToggle}
      >
        <div>
          <div className="config-card__title">
            {title}
          </div>

          <div className="config-card__subtitle">
            {subtitle}
          </div>
        </div>

        <span className="config-card__toggle">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open ? (
        <div className="config-card__body">
          {children}
        </div>
      ) : null}
    </article>
  );
}


function RadioGroup({
  name,
  value,
  options,
  onChange,
}) {
  return (
    <div className="config-inline">
      {options.map((option) => (
        <label
          key={option.value}
          className="config-choice"
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />

          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}


export default function NonPoConfigurationView() {
  const [config, setConfig] = useState(
    createDefaultConfig
  );

  const [openSections, setOpenSections] = useState(
    DEFAULT_OPEN_SECTIONS
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");


  useEffect(() => {
    let cancelled = false;

    async function loadConfiguration() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          "/api/configuration/",
          {
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const {
          isJson,
          payload,
          text,
        } = await parseJsonResponse(response);

        if (!isJson) {
          throw new Error(
            normalizeNonJsonError(
              response,
              text,
              "Unable to load saved configuration."
            )
          );
        }

        if (!cancelled) {
          if (payload.status === "success") {
            setConfig(
              mergeLoadedConfig(payload.data)
            );
          }
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.message ||
              "Unable to load saved configuration."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadConfiguration();

    return () => {
      cancelled = true;
    };
  }, []);


  function toggleSection(sectionKey) {
    setOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  }


  function updateTopLevel(key, value) {
    setConfig((current) => ({
      ...current,
      [key]: value,
    }));
  }


  function updateSection(sectionKey, field, value) {
    setConfig((current) => ({
      ...current,

      [sectionKey]: {
        ...current[sectionKey],
        [field]: value,
      },
    }));
  }


  function buildPayload() {
    return {
      invoiceType: config.invoiceType,

      duplicate_check: config.duplicate_check,

      companyPan: config.companyPan
        .trim()
        .toUpperCase(),

      material_service_codes: {
        material:
          config.material_service_codes.material,

        service:
          config.material_service_codes.service,
      },
    };
  }


  async function handleSave() {
    const pan = config.companyPan
      ?.trim()
      .toUpperCase();

    if (
      !pan ||
      !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid PAN",
        text: "Please enter a valid Company PAN.",
      });

      return;
    }

    setSaving(true);

    try {
      const csrfToken = await ensureCsrfToken();

      const formData = new FormData();

      formData.append(
        "payload",
        JSON.stringify(buildPayload())
      );

      const response = await fetch(
        "/api/configuration/save/",
        {
          method: "POST",

          headers: {
            "X-CSRFToken": csrfToken,
            Accept: "application/json",
          },

          credentials: "include",

          body: formData,
        }
      );

      const {
        isJson,
        payload,
        text,
      } = await parseJsonResponse(response);

      if (!isJson) {
        throw new Error(
          normalizeNonJsonError(
            response,
            text,
            "Unable to save configuration."
          )
        );
      }

      if (
        !response.ok ||
        payload.status !== "success"
      ) {
        throw new Error(
          payload.message ||
            "Unable to save configuration."
        );
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text:
          payload.message ||
          "Configuration saved successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          requestError.message ||
          "Unable to save configuration.",
        confirmButtonText: "OK",
      });

    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return (
      <div className="workspace-panel">
        Loading configuration…
      </div>
    );
  }


  return (
    <section className="config-view">

      <div className="config-view__header workspace-panel">
        <div>
          <p className="eyebrow">
            Basic Configurations
          </p>

          <h2>
            Accounting Configuration
          </h2>

          <p className="config-view__intro">
            Accounting Configuration enables
            administrators to configure key
            accounting rules, validations, and
            processing logic that govern
            transaction posting, compliance,
            invoice verification, and financial
            controls throughout the system.
          </p>
        </div>

        <button
          className="primary-button"
          type="button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? "Saving..."
            : "Submit Configuration"}
        </button>
      </div>


      {error ? (
        <div className="config-banner config-banner--error">
          {error}
        </div>
      ) : null}


      {/* -------------------------------------------------- */}
      {/* 1. TYPE OF INVOICES */}
      {/* -------------------------------------------------- */}

      <Section
        title="1. Type of Invoices"
        subtitle="What kind of invoices does the company have?"
        open={openSections.invoiceType}
        onToggle={() =>
          toggleSection("invoiceType")
        }
      >
        <div className="config-field">
          <label>
            Invoice Type
          </label>

          <select
            value={config.invoiceType}
            onChange={(event) =>
              updateTopLevel(
                "invoiceType",
                event.target.value
              )
            }
          >
            <option value="">
              Select invoice type
            </option>

            <option value="PO">
              PO
            </option>

            <option value="NonPO">
              NonPO
            </option>

            <option value="Both">
              Both
            </option>
          </select>
        </div>
      </Section>


      {/* -------------------------------------------------- */}
      {/* 2. DUPLICATE INVOICE */}
      {/* -------------------------------------------------- */}

      <Section
        title="2. Duplicate Invoice"
        subtitle="Duplicate checking rules"
        open={openSections.duplicate}
        onToggle={() =>
          toggleSection("duplicate")
        }
      >
        <div className="config-field">
          <label>
            Enable duplicate invoice check?
          </label>

          <RadioGroup
            name="dup_check"
            value={
              config.duplicate_check.dup_check
            }
            options={[
              {
                value: "Y",
                label: "Yes",
              },
              {
                value: "N",
                label: "No",
              },
            ]}
            onChange={(value) =>
              updateSection(
                "duplicate_check",
                "dup_check",
                value
              )
            }
          />
        </div>
      </Section>


      {/* -------------------------------------------------- */}
      {/* 3. COMPANY PAN */}
      {/* -------------------------------------------------- */}

      <Section
        title="3. Company PAN"
        subtitle="Enter PAN number of company"
        open={openSections.companyPan}
        onToggle={() =>
          toggleSection("companyPan")
        }
      >
        <div className="config-field">
          <label>
            Company PAN Number
          </label>

          <input
            value={config.companyPan}
            onChange={(event) =>
              updateTopLevel(
                "companyPan",
                event.target.value.toUpperCase()
              )
            }
            placeholder="ABCDE1234F"
          />
        </div>
      </Section>


      {/* -------------------------------------------------- */}
      {/* 4. MATERIAL / SERVICE CODES */}
      {/* -------------------------------------------------- */}

      <Section
        title="4. Material / Service Codes"
        subtitle="Enter comma-separated codes"
        open={openSections.materialService}
        onToggle={() =>
          toggleSection("materialService")
        }
      >
        <div className="config-grid">

          <div className="config-field">
            <label>
              Material Codes
            </label>

            <input
              value={config.material_service_codes.material.join(
                ", "
              )}
              onChange={(event) =>
                updateSection(
                  "material_service_codes",
                  "material",
                  event.target.value
                    .split(",")
                    .map((item) =>
                      item.trim().toUpperCase()
                    )
                    .filter(Boolean)
                )
              }
              placeholder="MAT001, MAT002"
            />
          </div>


          <div className="config-field">
            <label>
              Service Codes
            </label>

            <input
              value={config.material_service_codes.service.join(
                ", "
              )}
              onChange={(event) =>
                updateSection(
                  "material_service_codes",
                  "service",
                  event.target.value
                    .split(",")
                    .map((item) =>
                      item.trim().toUpperCase()
                    )
                    .filter(Boolean)
                )
              }
              placeholder="SER001, SER002"
            />
          </div>

        </div>
      </Section>

    </section>
  );
}